import { useEffect, useMemo, useState } from 'react';
import { Building2, Search, UserCheck } from 'lucide-react';
import type { Partner, User, UserRole } from '@doorly/shared';
import { USER_ROLE_ORDER } from '@doorly/shared';
import { db } from '../db/schema';
import { DialogHeader } from './ui/DialogHeader';
import { useApiQuery } from '../lib/api';
import { track } from '../lib/analytics';
import { setImpersonation } from '../lib/impersonation';
import { Avatar, initialsFrom } from './ui/avatar';
import { Pill } from './ui/pill';
import { cn } from '../lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ROLE_TONE: Record<UserRole, 'blue' | 'violet' | 'amber' | 'green' | 'slate'> = {
  admin: 'violet',
  responsable_partenaire: 'amber',
  responsable_backoffice: 'amber',
  manager: 'blue',
  superviseur: 'green',
  vendeur: 'slate',
};

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  responsable_partenaire: 'Resp. partenaire',
  responsable_backoffice: 'Resp. back-office',
  manager: 'Manager',
  superviseur: 'Superviseur',
  vendeur: 'Vendeur',
};

/** Ordre d'affichage : du haut de la hiérarchie vers le terrain. */
const ROLE_ORDER: readonly UserRole[] = USER_ROLE_ORDER;

/** Le dernier filtre réseau survit à la fermeture : on teste rarement un seul compte. */
const PARTNER_FILTER_KEY = 'doorly:imperso:partnerFilter';

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Modal admin uniquement — liste tous les utilisateurs visibles côté API
 * (`GET /users` est déjà admin-large) et permet de basculer en imperso.
 * Picking quelqu'un wipe Dexie + redirige sur '/' — la session backend
 * suit via le header `X-Impersonate-As`.
 *
 * En admin, vendeurs et superviseurs arrivent **anonymisés** (nom = matricule,
 * email `@anonymized.doorly`) : chercher « Paulo » ne trouve rien, et c'est
 * normal. D'où les deux filtres — réseau et rôle — qui permettent d'arriver
 * au bon compte sans connaître son nom, et la recherche qui accepte le
 * matricule et le nom du réseau.
 */
export function ImpersonationPicker({ open, onClose }: Props) {
  const [q, setQ] = useState('');
  const [role, setRole] = useState<UserRole | 'all'>('all');
  const [partnerId, setPartnerId] = useState<string>(() => {
    try {
      return sessionStorage.getItem(PARTNER_FILTER_KEY) ?? 'all';
    } catch {
      return 'all';
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(PARTNER_FILTER_KEY, partnerId);
    } catch {
      /* stockage indisponible : le filtre ne survit pas, rien de plus */
    }
  }, [partnerId]);

  const users = useApiQuery<User[]>(['users', 'all'], '/users');
  const partners = useApiQuery<Partner[]>(['partners'], '/partners');
  const partnerById = useMemo(
    () => new Map((partners.data ?? []).map((p) => [p.id, p])),
    [partners.data],
  );

  // Réseaux triés par nom, avec leur effectif — les réseaux vides ou archivés
  // restent visibles mais en queue : on veut pouvoir tester un réseau neuf.
  const partnerOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const u of users.data ?? []) counts.set(u.partnerId, (counts.get(u.partnerId) ?? 0) + 1);
    return (partners.data ?? [])
      .map((p) => ({ id: p.id, name: p.name, archived: p.archived, count: counts.get(p.id) ?? 0 }))
      .sort(
        (a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name, 'fr'),
      );
  }, [partners.data, users.data]);

  // Effectif par rôle dans le réseau choisi — les chips annoncent ce qu'elles
  // vont montrer, un rôle absent est grisé plutôt que de mener à « aucun ».
  const inPartner = useMemo(
    () => (users.data ?? []).filter((u) => partnerId === 'all' || u.partnerId === partnerId),
    [users.data, partnerId],
  );
  const roleCounts = useMemo(() => {
    const m = new Map<UserRole, number>();
    for (const u of inPartner) m.set(u.role, (m.get(u.role) ?? 0) + 1);
    return m;
  }, [inPartner]);

  const list = useMemo(() => {
    const needle = normalize(q.trim());
    return inPartner
      .filter((u) => role === 'all' || u.role === role)
      .filter((u) => {
        if (!needle) return true;
        const partnerName = partnerById.get(u.partnerId)?.name ?? '';
        return normalize(
          `${u.firstName} ${u.lastName} ${u.email} ${u.matricule} ${partnerName} ${ROLE_LABEL[u.role]}`,
        ).includes(needle);
      })
      .sort(
        (a, b) =>
          (partnerById.get(a.partnerId)?.name ?? '').localeCompare(
            partnerById.get(b.partnerId)?.name ?? '',
            'fr',
          ) ||
          ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) ||
          a.lastName.localeCompare(b.lastName, 'fr'),
      );
  }, [inPartner, role, q, partnerById]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        className="fixed inset-x-3 top-6 bottom-6 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[560px] md:h-[min(80vh,720px)] z-[70] bg-surface flex flex-col overflow-hidden"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-elevated)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Imperso utilisateur"
      >
        <DialogHeader title="Imperso utilisateur" icon={UserCheck} onClose={onClose} />

        <div className="px-4 py-3 border-b border-hair space-y-2.5">
          {/* Réseau d'abord : c'est le premier tri mental (« le compte de test
              de tel réseau »), et il conditionne les effectifs des rôles. */}
          <div className="relative">
            <Building2 className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-mute" />
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="w-full appearance-none bg-surface-alt rounded-[var(--radius-sm)] pl-8 pr-8 py-2 text-body border border-hair focus:border-brand focus:outline-none"
              aria-label="Filtrer par réseau"
            >
              <option value="all">Tous les réseaux · {users.data?.length ?? 0}</option>
              {partnerOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.archived ? ' (archivé)' : ''} · {p.count}
                </option>
              ))}
            </select>
          </div>

          <div
            className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1"
            role="group"
            aria-label="Filtrer par rôle"
          >
            <RoleChip
              active={role === 'all'}
              onClick={() => setRole('all')}
              count={inPartner.length}
            >
              Tous
            </RoleChip>
            {ROLE_ORDER.map((r) => (
              <RoleChip
                key={r}
                active={role === r}
                onClick={() => setRole(r)}
                count={roleCounts.get(r) ?? 0}
                disabled={(roleCounts.get(r) ?? 0) === 0}
              >
                {ROLE_LABEL[r]}
              </RoleChip>
            ))}
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-mute" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, matricule, email, réseau…"
              className="w-full bg-surface-alt rounded-[var(--radius-sm)] pl-8 pr-3 py-2 text-body border border-hair focus:border-brand focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {users.isLoading && <p className="p-4 text-body text-ink-mute">Chargement…</p>}
          {users.error && (
            <p className="p-4 text-body text-danger">
              {users.error instanceof Error ? users.error.message : String(users.error)}
            </p>
          )}
          {list.map((u, i) => {
            const partnerName = partnerById.get(u.partnerId)?.name;
            const prevPartner = i > 0 ? list[i - 1]!.partnerId : null;
            const showHeader = partnerId === 'all' && u.partnerId !== prevPartner;
            // En admin, un vendeur anonymisé porte son matricule en guise de
            // nom : inutile de l'afficher deux fois.
            const isAnonView = u.email.endsWith('@anonymized.doorly');
            return (
              <div key={u.id}>
                {showHeader && (
                  <div className="sticky top-0 z-10 bg-surface-alt/95 backdrop-blur px-4 py-1.5 text-micro font-semibold uppercase tracking-[0.08em] text-ink-mute border-b border-hair">
                    {partnerName ?? 'Réseau inconnu'}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => startImpersonation(u)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-alt border-b border-divider text-left"
                >
                  <Avatar initials={initialsFrom(u.firstName, u.lastName)} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="text-body font-bold text-ink truncate flex items-center gap-2">
                      <span className="truncate">
                        {u.firstName} {u.lastName}
                      </span>
                      {!isAnonView && (
                        <span className="text-micro text-ink-soft font-semibold shrink-0">
                          {u.matricule}
                        </span>
                      )}
                    </div>
                    <div className="text-micro text-ink-mute truncate">
                      {isAnonView ? 'Compte anonymisé — identifié par son matricule' : u.email}
                    </div>
                  </div>
                  <Pill tone={ROLE_TONE[u.role] ?? 'slate'}>{ROLE_LABEL[u.role]}</Pill>
                </button>
              </div>
            );
          })}
          {!users.isLoading && list.length === 0 && (
            <div className="p-6 text-center space-y-1">
              <p className="text-body text-ink-mute">Aucun utilisateur ne correspond.</p>
              <p className="text-micro text-ink-mute">
                Les vendeurs et superviseurs vous apparaissent sous leur matricule, pas sous leur
                nom : filtrez par réseau et par rôle, ou cherchez le matricule.
              </p>
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-hair text-micro text-ink-mute">
          {list.length} compte{list.length > 1 ? 's' : ''}
          {partnerId !== 'all' && ` · ${partnerById.get(partnerId)?.name ?? ''}`}
          {role !== 'all' && ` · ${ROLE_LABEL[role]}`}
        </div>
      </div>
    </>
  );
}

function RoleChip({
  active,
  disabled,
  count,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled && !active}
      className={cn(
        'shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-meta font-semibold border transition-colors',
        active
          ? 'bg-brand text-brand-ink border-brand'
          : 'bg-surface text-ink-soft border-hair hover:border-ink-mute disabled:opacity-40 disabled:hover:border-hair',
      )}
    >
      {children}
      <span
        className={cn('tabular-nums text-micro', active ? 'text-brand-ink/80' : 'text-ink-mute')}
      >
        {count}
      </span>
    </button>
  );
}

async function startImpersonation(u: User) {
  track('imperso_started', {
    targetRole: u.role,
    targetPartnerId: u.partnerId ?? null,
  });
  setImpersonation(u.id, `${u.firstName} ${u.lastName}`);
  // wipe() vide les tables Dexie dans une transaction — plus fiable que
  // delete() qui peut racer avec le reload.
  await db.wipe();
  window.location.href = '/';
}
